<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <!-- Left Text-->
      <b-col
        lg="7"
        class="d-none d-lg-flex align-items-center p-5"
      >
        <div class="w-100 d-lg-flex align-items-center justify-content-center px-5">
          <b-img
            fluid
            :src="imgUrl"
            alt="Password Setup"
          />
        </div>
      </b-col>
      <!-- /Left Text-->

      <!-- Password Setup-->
      <b-col
        lg="5"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <b-card-title
            title-tag="h2"
            class="font-weight-bold mb-1"
          >
            Şifre Belirleme 🔐
          </b-card-title>
          <b-card-text class="mb-3">
            Hesabınızın güvenliği için güçlü bir şifre belirleyiniz.
          </b-card-text>

          <!-- Password Setup form -->
          <validation-observer ref="passwordSetupValidation">
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent
            >
              <!-- New Password -->
              <b-form-group>
                <label for="new-password">Yeni Şifre</label>
                <validation-provider
                  #default="{ errors }"
                  name="NewPassword"
                  vid="NewPassword"
                  rules="required|min:8|strong-password"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid':null"
                  >
                    <b-form-input
                      id="new-password"
                      v-model="newPassword"
                      :state="errors.length > 0 ? false:null"
                      class="form-control-merge"
                      :type="newPasswordFieldType"
                      placeholder="En az 8 karakter"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="newPasswordToggleIcon"
                        @click="toggleNewPasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
                <small class="text-muted">
                  Şifre en az 8 karakter olmalı ve büyük harf, küçük harf, rakam içermelidir.
                </small>
              </b-form-group>

              <!-- Confirm Password -->
              <b-form-group>
                <label for="confirm-password">Şifre Tekrarı</label>
                <validation-provider
                  #default="{ errors }"
                  name="ConfirmPassword"
                  vid="ConfirmPassword"
                  rules="required"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid':null"
                  >
                    <b-form-input
                      id="confirm-password"
                      v-model="confirmPassword"
                      :state="errors.length > 0 ? false : (confirmPassword.length > 0 ? passwordsMatch : null)"
                      class="form-control-merge"
                      :type="confirmPasswordFieldType"
                      placeholder="Şifrenizi tekrar giriniz"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="confirmPasswordToggleIcon"
                        @click="toggleConfirmPasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small v-if="confirmPassword.length > 0 && !passwordsMatch" class="text-danger d-block">
                    Şifreler eşleşmiyor
                  </small>
                  <small v-if="confirmPassword.length > 0 && passwordsMatch" class="text-success d-block">
                    Şifreler eşleşiyor ✓
                  </small>
                </validation-provider>
              </b-form-group>

              <!-- Password Strength Indicator -->
              <div class="password-strength-wrapper mb-3">
                <div class="password-strength-label">
                  <small class="text-muted">Şifre Gücü:</small>
                  <small :class="passwordStrengthClass">{{ passwordStrengthText }}</small>
                </div>
                <b-progress
                  :value="passwordStrengthScore"
                  :variant="passwordStrengthVariant"
                  height="4px"
                  class="mt-1"
                />
              </div>

              <!-- Submit button -->
              <b-button
                type="button"
                variant="primary" 
                class="d-flex align-items-center justify-content-center"
                block
                :disabled="loading || !passwordsMatch || newPassword.length < 8"
                @click="setupPassword"
              >
                <b-spinner small class="mr-50" v-if="loading"></b-spinner>
                Şifreyi Kaydet
              </b-button>
            </b-form>
          </validation-observer>
        </b-col>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BRow, BCol, BFormGroup, BFormInput, BInputGroupAppend, BInputGroup, 
  BCardText, BCardTitle, BImg, BForm, BButton, BSpinner, BProgress
} from 'bootstrap-vue'
import { required, strongPassword } from '@validations'
import store from '@/store/index'
import { AuthApi } from '@/api/auth'
import Auth from '@/utilities/auth'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BSpinner,
    BProgress,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      newPassword: '',
      confirmPassword: '',
      newPasswordFieldType: 'password',
      confirmPasswordFieldType: 'password',
      loading: false,
      sideImg: require('@/assets/images/logo/hnz-logo.png'),
      required,
      strongPassword,
      customerCode: '',
    }
  },
  computed: {
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        this.sideImg = require('@/assets/images/logo/hnz-logo.png')
        return this.sideImg
      }
      return this.sideImg
    },
    newPasswordToggleIcon() {
      return this.newPasswordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    confirmPasswordToggleIcon() {
      return this.confirmPasswordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    passwordStrengthScore() {
      const password = this.newPassword
      if (!password) return 0
      
      let score = 0
      if (password.length >= 8) score += 25
      if (/[a-z]/.test(password)) score += 25
      if (/[A-Z]/.test(password)) score += 25
      if (/[0-9]/.test(password)) score += 25
      
      return score
    },
    passwordStrengthVariant() {
      const score = this.passwordStrengthScore
      if (score < 50) return 'danger'
      if (score < 75) return 'warning'
      return 'success'
    },
    passwordStrengthText() {
      const score = this.passwordStrengthScore
      if (score === 0) return 'Şifre giriniz'
      if (score < 50) return 'Zayıf'
      if (score < 75) return 'Orta'
      return 'Güçlü'
    },
    passwordStrengthClass() {
      const score = this.passwordStrengthScore
      if (score < 50) return 'text-danger'
      if (score < 75) return 'text-warning'
      return 'text-success'
    },
    passwordsMatch() {
      if (!this.newPassword || !this.confirmPassword) return false
      return this.newPassword.trim() === this.confirmPassword.trim()
    }
  },
  mounted() {
    // Customer token kontrolü
    const customerToken = localStorage.getItem('customerToken')
    const customerCode = localStorage.getItem('customerCode')
    
    if (!customerToken) {
      this.$router.push({ name: 'login' })
      return
    }
    
    if (customerCode) {
      this.customerCode = customerCode
    }
  },
  methods: {
    toggleNewPasswordVisibility() {
      this.newPasswordFieldType = this.newPasswordFieldType === 'password' ? 'text' : 'password'
    },
    toggleConfirmPasswordVisibility() {
      this.confirmPasswordFieldType = this.confirmPasswordFieldType === 'password' ? 'text' : 'password'
    },
    setupPassword() {
      // İlk olarak customer kodu kontrolü
      if (!this.customerCode) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Cari kodu bulunamadı. Lütfen tekrar giriş yapınız.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        this.$router.push({ name: 'login' })
        return
      }
      
      // İlk olarak şifrelerin boş olmadığını kontrol et
      if (!this.newPassword || !this.confirmPassword) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Lütfen tüm alanları doldurunuz.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return
      }
      
      // Manuel şifre kontrolü
      if (this.newPassword !== this.confirmPassword) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Şifreler uyuşmuyor. Lütfen kontrol ediniz.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return
      }
      
      // Şifre uzunluk kontrolü
      if (this.newPassword.length < 8) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Şifre en az 8 karakter olmalıdır.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return
      }
      
      this.$refs.passwordSetupValidation.validate().then(success => {
        if (success) {
          this.loading = true
          
          console.log('Sending data:', {
            customerCode: this.customerCode,
            password: this.newPassword,
            confirmPassword: this.confirmPassword
          })
          
          AuthApi.SetCustomerPassword({
            customerCode: this.customerCode,
            password: this.newPassword,
            confirmPassword: this.confirmPassword
          })
          .then((res) => {
            this.$result(res)
            this.loading = false
            if (res.data.Success) {
              // Şifre belirleme başarılı olduğunda token'ı güncelle
              if (res.data.Data && res.data.Data.Token) {
                Auth.customerToken = res.data.Data.Token
              }
              
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Şifreniz başarıyla kaydedildi.',
                  icon: 'CheckCircleIcon',
                  variant: 'success',
                },
              })
              
              // Redirect to appropriate page after successful password setup
              setTimeout(() => {
                try {
                  // Customer için uygun sayfaya yönlendir
                  const customerCode = localStorage.getItem('customerCode')
                  if (customerCode) {
                    // Customer account detail sayfasına yönlendir
                    this.$router.push({
                      name: "customer-account-detail", 
                      params: { code: customerCode }
                    })
                  } else {
                    // Customer kodu yoksa login'e geri dön
                    this.$router.push({ name: "login" })
                  }
                } catch (error) {
                  console.log('Navigation error:', error)
                  // Fallback: login sayfasına yönlendir
                  window.location.href = '/Auth/Login'
                }
              }, 1500)
            }
          })
          .catch((err) => {
            this.loading = false
            this.$result(null, err)
          })
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/pages/page-auth.scss';

.password-strength-wrapper {
  .password-strength-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.btn-primary {
  background: linear-gradient(135deg, #08a1ae 0%, #52b0b8 100%);
  border: none;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(8, 161, 174, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
}

.form-control:focus {
  border-color: #08a1ae;
  box-shadow: 0 0 0 0.2rem rgba(8, 161, 174, 0.25);
}
</style>
